import React, {FC, ReactElement} from "react";
import {FieldArray} from "formik";
import {Card, CardContent, CardHeader, Checkbox, FormControlLabel, FormGroup, Grid} from "@mui/material";
import {observer} from "mobx-react";
import {AlertColor} from "@mui/material/Alert/Alert";
import {FormikProps} from "formik/dist/types";
import {AddressDto, GetCompanySettingsDto} from "../../../model/company/GetCompanySettingsDto";
import {BankDetailsOnInvoiceDto} from "../../../model/company/InvoiceDataDto";
import {Trans, useTranslation} from "react-i18next";
import {BankDetailsComponent} from "./BankDetailsComponent";
import TextFieldWithValidation from "../../../components/input/TextFieldWithValidation";
import CountrySelector from "../../../components/selector/CountrySelector";

export const InvoiceDetailsCard: FC<{
    formik: FormikProps<GetCompanySettingsDto>
    showSnackBar: (messageType: AlertColor, messageText: string) => void
}> = observer(({formik, showSnackBar}): ReactElement => {
    const invoiceData = formik.values.invoiceData;
    const bankDetails: BankDetailsOnInvoiceDto[] = invoiceData ? invoiceData.bankDetails ?? [] : [];
    const invoiceAddress = invoiceData?.invoiceAddress

    const {t} = useTranslation();

    const onUseDefaultAddressChanged = (e: any) => {
        if (e.target.checked) {
            formik.setFieldValue("invoiceData.invoiceAddress", null);
        } else {
            formik.setFieldValue("invoiceData.invoiceAddress", {});
        }
    };
    // @ts-ignore
    const errors: FormikErrors<AddressDto> | undefined = formik.errors.invoiceData?.invoiceAddress;
    return <>
        <Card>
            <CardHeader subheader={<Trans i18nKey={`company_settings.invoice.header_address`}>Address</Trans>}/>
            <CardContent style={{paddingBottom: "0rem", paddingTop: "0rem"}}>
                <Grid container>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox checked={!invoiceAddress} onChange={onUseDefaultAddressChanged}/>}
                                              label={t(`company_settings.invoice.use_legal_address`, "Use legal address")}
                            />
                        </FormGroup>
                    </Grid>
                    {invoiceAddress && <Grid item xs={12}>
                        <Grid container spacing={1}>
                            <Grid xs={12} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="invoiceData.invoiceAddress.address"
                                        label={t(`company_settings.invoice.invoice_address`, "Invoice Address")}
                                        placeholder="Lossi plats 1a-11"
                                        value={invoiceAddress?.address}
                                        fullWidth
                                        onChange={formik.handleChange}
                                />
                            </Grid>

                            <Grid xs={6} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="invoiceData.invoiceAddress.city"
                                        label={t(`company_settings.invoice.city`, "City")}
                                        placeholder="Tallinn"
                                        value={invoiceAddress?.city}
                                        fullWidth
                                        onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid xs={6} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="invoiceData.invoiceAddress.postalCode"
                                        label={t(`company_settings.invoice.post_code`, "Postal code")}
                                        placeholder="10137"
                                        value={invoiceAddress?.postalCode}
                                        fullWidth
                                        onChange={formik.handleChange}
                                        hasError={errors?.postalCode}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <CountrySelector
                                        label={t(`company_settings.invoice.country`, "Country")}
                                        placeholder={t(`company_settings.invoice.country`, "Country")}
                                        value={invoiceAddress?.countryCode}
                                        hasError={
                                            // @ts-ignore
                                            errors?.countryCode
                                        }
                                        errorText={t(`company_settings.invoice.country_error`, "Please fill in invoice country")}
                                        onChanged={code => formik.setFieldValue("invoiceData.invoiceAddress.countryCode", code, true)}

                                />
                            </Grid>
                            <Grid xs={6} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="invoiceData.invoiceAddress.email"
                                        label={t(`company_settings.invoice.email`, "Email")}
                                        placeholder="info@company.com"
                                        value={invoiceAddress?.email}
                                        fullWidth
                                        onChange={formik.handleChange}
                                        hasError={errors?.email}
                                        errorText={t(`company_settings.invoice.email_error`, "Email address is invalid")}
                                />
                            </Grid>
                            <Grid xs={6} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="invoiceData.invoiceAddress.phoneNo"
                                        label={t(`company_settings.invoice.phone_num`, "Phone number")}
                                        placeholder="+372 5555 8888"
                                        value={invoiceAddress?.phoneNo}
                                        fullWidth
                                        onChange={formik.handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Grid>}
                </Grid>
            </CardContent>
        </Card>
        <Card>
            <CardHeader subheader={<Trans i18nKey={`company_settings.invoice.header_banks`}>Banks</Trans>}/>
            <CardContent>
                <Grid container>
                    <FieldArray name="invoiceData.bankDetails"
                                render={(arrHelper) => {
                                    return <>
                                        {bankDetails.map((it, index) => {
                                            return <Grid key={index} item>
                                                <BankDetailsComponent arrHelper={arrHelper}
                                                                      bankDetails={bankDetails}
                                                                      existingItem={it}
                                                                      showSnackBar={showSnackBar}/>
                                            </Grid>
                                        })
                                        }
                                        <Grid item>
                                            <BankDetailsComponent arrHelper={arrHelper} bankDetails={bankDetails}
                                                                  existingItem={undefined}
                                                                  showSnackBar={showSnackBar}/>
                                        </Grid>
                                    </>
                                }}
                    />
                </Grid>
            </CardContent>
        </Card>
    </>
});